import React, {
  Dispatch,
  FC,
  ReactElement,
  ReactNode,
  SetStateAction,
  useState,
  useEffect,
} from 'react';
import { AppBar, ListItemIcon, makeStyles, Menu, MenuItem, Toolbar } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import UsbOutlined from '@material-ui/icons/UsbOutlined';
import SettingsInputHdmiIcon from '@material-ui/icons/SettingsInputHdmi';
import MoreIcon from '@material-ui/icons/MoreVert';
import { PowerSettingsNew } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { DeviceType, listenSerialPort, openSerialPort } from '../services/serial';
import { logout as logoutAction } from '../actions/auth';
import WarehouseSelector from './warehouseSelector';
import { fetchActualClient } from '../services/client';

const useStyles = makeStyles(() => ({
  grow: {
    flexGrow: 1,
  },
  zoomedQrCode: {
    cursor: 'pointer',
    height: 400,
    position: 'absolute',
    right: 96,
    bottom: 16,
    width: 400,
  },
  qrCode: {
    cursor: 'pointer',
    height: 40,
    marginRight: 16,
    width: 40,
  },
}));

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  logout(): void;
  setSnackBarMessage: Dispatch<SetStateAction<ReactNode>>;
}

const DrawerAppBar: FC<Props> = ({
  open,
  setOpen,
  logout,
  setSnackBarMessage,
}: Props): ReactElement => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [port, setPort] = useState<SerialPort | null>();
  const [deisterReaderPort, setDeisterReaderPort] = useState<SerialPort | null>();
  const handleClose = () => setAnchorEl(null);
  const [actualClient, setActualClient] = useState<{ email: string } | null>(null);
  useEffect(() => {
    fetchActualClient()
      .then(data => {
        setActualClient({ email: data.email });
      })
      .catch(err => console.error('Failed to fetch actual client', err)); // eslint-disable-line no-console
  }, []);

  const onPortError = (deviceType: DeviceType, err: string | Error): void => {
    console.error(`Error on ${deviceType} port`, err); // eslint-disable-line no-console
    if (err === 'Serial undefined') {
      if (deviceType === 'DEISTER_BADGE_READER') {
        setDeisterReaderPort(undefined);
      } else if (deviceType === 'FOODLES_SCANNER') {
        setPort(undefined);
      }

      setSnackBarMessage(
        <span>
          Activez la fonctionnalité expérimentale ici{' '}
          <u>chrome://flags/#enable-experimental-web-platform-features</u>
        </span>,
      );
    }
  };

  return (
    <AppBar position="absolute" color="primary">
      <Toolbar>
        <IconButton color="inherit" onClick={() => setOpen(!open)} data-testid="fold">
          {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        <div className={classes.grow} />
        <Typography variant="body1" color="inherit" style={{ marginRight: 16 }}>
          Account : {actualClient?.email}
        </Typography>
        <WarehouseSelector />
        <IconButton
          size="small"
          color="inherit"
          disabled={!!port}
          data-testid="open-serial-port"
          onClick={() => {
            handleClose();
            openSerialPort()
              .then(serialPort => {
                setPort(serialPort);
                setSnackBarMessage('Lecteur de badge connecté !');
                listenSerialPort(serialPort, 'FOODLES_SCANNER', err =>
                  onPortError('FOODLES_SCANNER', err),
                );
              })
              .catch(err => {
                onPortError('FOODLES_SCANNER', err);
              });
          }}
        >
          <UsbOutlined />
        </IconButton>
        <IconButton edge="end" color="inherit" onClick={event => setAnchorEl(event.currentTarget)}>
          <MoreIcon />
        </IconButton>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem
            disabled={!!deisterReaderPort}
            data-testid="open-ca-serial-port"
            onClick={() => {
              handleClose();
              openSerialPort(9600)
                .then(serialPort => {
                  setDeisterReaderPort(serialPort);
                  setSnackBarMessage('Lecteur de badge CA connectée !');
                  listenSerialPort(serialPort, 'DEISTER_BADGE_READER', err =>
                    onPortError('DEISTER_BADGE_READER', err),
                  );
                })
                .catch(err => {
                  onPortError('DEISTER_BADGE_READER', err);
                });
            }}
          >
            <ListItemIcon>
              <SettingsInputHdmiIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Utiliser badgeuse CA</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              logout();
              handleClose();
            }}
          >
            <ListItemIcon>
              <PowerSettingsNew fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Logout</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

function mapDispatchToProps(dispatch: Dispatch<Action>): Pick<Props, 'logout'> {
  return {
    logout: () => dispatch(logoutAction()),
  };
}

export default connect(null, mapDispatchToProps)(DrawerAppBar);
